<template>
  <Main>
    <ToolsPanel
      :breadCrumbFirst="'Keywords'"
      :breadCrumbFirstLink="getFirstLink"
      :breadCrumbSecond="'Keywords Edit'"
      :title="'Keywords Edit'"
    >
      <div class="form-create__buttons">
        <MainButton buttonWhite @click.native="handleOpenCopyModal">Copy</MainButton>
        <MainButton buttonWhite @click.native="handleSubmit">Update</MainButton>
        <MainButton @click.native="handleBackToList">Back</MainButton>
      </div>
    </ToolsPanel>
    <ContentContainer>
      <div class="form-create">
        <form @submit.prevent="handleSubmit">
          <p class="form-create__label">Keyword</p>
          <div class="input-container relative">
            <InputText
              :set-value="formDataToSend"
              :id="'keyword'"
              :label="'keyword'"
              :type="'text'"
              :placeholder="'Keyword'"
              :error-messages="validationErrors['keyword']"
              isBordered
            />
            <a
              target="_blank"
              v-if="keyword && keyword.ahrefsLink"
              :href="keyword.ahrefsLink"
              class="ahrefs-link"
            >
              Ahrefs
            </a>
          </div>

          <p class="form-create__label">Parent Keyword</p>
          <div class="input-container">
            <div class="form-create__keyword">
              <input
                type="text"
                @focus="handleFocusParent"
                @blur="handleBlurParent"
                @input="debouncedGetParent"
                v-model="formDataToSend.parent_name"
                class="form-create__keyword-input"
              />

              <div v-if="parentList" class="form-create__keyword-list">
                <template v-if="parentArray.length > 0">
                  <div
                    @click="handleChooseItemParent(item)"
                    v-for="item in parentArray"
                    :key="item.id"
                    class="form-create__keyword-item"
                  >
                    {{ item.keyword.toLowerCase() }}
                  </div>
                </template>
                <div v-else class="form-create__keyword-item">NO keywords</div>
              </div>
            </div>

            <div
              @click="deleteParent"
              v-if="formDataToSend.parent_id"
              class="form-create__delete-parent"
            >
              Delete Parent
            </div>

            <Notification :error-messages="validationErrors['keywordId']" />
          </div>

          <p class="form-create__label">Categories</p>
          <input
            v-model="categorySearch"
            class="form-create__search"
            type="text"
            placeholder="Search category..."
            @input="debouncedGetCategories"
          />
          <div class="form-create__label" style="padding-top: 10px" v-if="keyword.category[0]">
            <router-link :to="handleToCategory(keyword.category[0].id)">
              {{ keyword.category[0].fullName }}
            </router-link>
          </div>

          <div class="input-container checkbox-container checkbox-container_margin">
            <div class="checkbox-container__item">
              <InputRadio
                :set-value="formDataToSend"
                :id="'category'"
                :values="categoriesList"
                :name="'fullName'"
                :value="'id'"
              />
            </div>
          </div>

          <Notification :error-messages="validationErrors['categoryId']" />

          <p class="form-create__label">Attributes</p>
          <div class="form-create__chosen-items">
            <div
              v-for="(item, index) in formDataToSend.attributes"
              :key="index"
              class="form-create__chosen-item"
            >
              {{ item.name }} (seo: {{ item.seoName }})
              <span v-if="!item.parentId"><b>is parent</b></span>

              <div @click="deleteAttribute(index)" class="form-create__check-item-add">-</div>
            </div>
          </div>

          <input
            v-model="attributeSearch"
            class="form-create__search"
            type="text"
            placeholder="Search attribute..."
            @input="debouncedGetAttributes"
          />

          <div class="input-container checkbox-container checkbox-container_margin">
            <div v-for="(item, index) in attributesTree" :key="index">
              <div class="form-create__check-item">
                <b>{{ item.name }} (seo: {{ item.seoName }})</b>
                <div @click="addAttribute(item)" class="form-create__check-item-add">+</div>
              </div>
              <div v-if="item.children.length">
                <div
                  v-for="(el, i) in item.children[0]"
                  :key="i"
                  class="form-create__check-item child"
                >
                  {{ el.name }} (seo: {{ el.seoName }})
                  <div @click="addAttribute(el)" class="form-create__check-item-add">+</div>
                </div>
              </div>
            </div>
          </div>

          <p class="form-create__label">Formats</p>
          <div class="input-container checkbox-container">
            <div v-for="item in templatesTypesList" :key="item.id" class="checkbox-container__item">
              <MainCheckbox
                :label="item.name"
                :id="item.id + ''"
                :set-value="formDataToSend.type"
              />
            </div>
          </div>

          <p class="form-create__label">Sizes</p>
          <div class="input-container checkbox-container">
            <div v-for="item in templatesSizesList" :key="item.id" class="checkbox-container__item">
              <MainCheckbox
                :label="item.name"
                :id="item.id + ''"
                :set-value="formDataToSend.size"
              />
            </div>
          </div>

          <p class="form-create__label">Seasonality</p>
          <div class="input-container">
            <div v-for="item in months" :key="item.id" class="checkbox-container__item">
              <MainCheckbox
                :label="item.name"
                :id="item.id + ''"
                :set-value="formDataToSend.months"
              />
            </div>
          </div>

          <p class="form-create__label">Key Points</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('key_points')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <InputTextarea
              :set-value="formDataToSend"
              :id="'key_points'"
              :label="'key_points'"
              :placeholder="''"
              :error-messages="validationErrors['keyPoints']"
              isBordered
            />
          </div>

          <p class="form-create__label">Indents and Margins</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('indents_and_margins')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <InputText
              :set-value="formDataToSend"
              :id="'indents_and_margins'"
              :label="'indents_and_margins'"
              :type="'text'"
              :placeholder="'Indents and Margins'"
              :error-messages="validationErrors['indentsAndMargins']"
              isBordered
            />
          </div>

          <p class="form-create__label">Images</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('images')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <InputText
              :set-value="formDataToSend"
              :id="'images'"
              :label="'images'"
              :type="'text'"
              :placeholder="'images'"
              :error-messages="validationErrors['images']"
              isBordered
            />
          </div>

          <p class="form-create__label">Typography</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('typography')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <TextEditor
              :set-value="formDataToSend"
              :id="'typography'"
              :placeholder="''"
              :error-messages="validationErrors['typography']"
            />
          </div>

          <p class="form-create__label">Template Overview</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('template_overview')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <TextEditor
              :set-value="formDataToSend"
              :id="'template_overview'"
              :placeholder="''"
              :error-messages="validationErrors['templateOverview']"
            />
          </div>

          <p class="form-create__label">Preview Requirements</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('preview_requirements')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <InputTextarea
              :set-value="formDataToSend"
              :id="'preview_requirements'"
              :label="'preview_requirements'"
              :placeholder="''"
              :error-messages="validationErrors['preview_requirements']"
              isBordered
            />
          </div>

          <p class="form-create__label">Examples</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('examples')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <InputTextarea
              :set-value="formDataToSend"
              :id="'examples'"
              :label="'examples'"
              :placeholder="''"
              :error-messages="validationErrors['examples']"
              isBordered
            />
          </div>

          <p class="form-create__label">Pages</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('pages')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <InputText
              :set-value="formDataToSend"
              :id="'pages'"
              :label="'pages'"
              :type="'text'"
              :placeholder="'Pages'"
              :error-messages="validationErrors['pages']"
              isBordered
            />
          </div>

          <p class="form-create__label">Target audience</p>
          <div
            v-if="keyword && keyword.attributesFromParent.includes('target_audience')"
            class="form-create__info"
          >
            Parent keyword requirements are output
          </div>
          <div class="input-container">
            <InputTextarea
              :set-value="formDataToSend"
              :id="'target_audience'"
              :label="'target_audience'"
              :placeholder="''"
              :error-messages="validationErrors['target_audience']"
              isBordered
            />
          </div>

          <div
            @click="toggleAllFields"
            :class="['form-create__all-button', isAllFields && 'active']"
          >
            See All Fields
            <IconSelect />
          </div>

          <!-- No active field -->

          <transition name="accordion" @before-enter="beforeEnter" @enter="enter" @leave="leave">
            <div v-if="isAllFields" class="form-create__all-container">
              <p class="form-create__label">Template Requirements</p>
              <div
                v-if="keyword && keyword.attributesFromParent.includes('template_requirements')"
                class="form-create__info"
              >
                Parent keyword requirements are output
              </div>
              <div class="input-container">
                <InputTextarea
                  :set-value="formDataToSend"
                  :id="'template_requirements'"
                  :label="'template_requirements'"
                  :placeholder="''"
                  :error-messages="validationErrors['templateRequirements']"
                  isBordered
                />
              </div>

              <p class="form-create__label">Template Diagram</p>
              <div
                v-if="keyword && keyword.attributesFromParent.includes('template_diagram')"
                class="form-create__info"
              >
                Parent keyword requirements are output
              </div>
              <div class="input-container">
                <InputTextarea
                  :set-value="formDataToSend"
                  :id="'template_diagram'"
                  :label="'template_diagram'"
                  :placeholder="''"
                  :error-messages="validationErrors['templateDiagram']"
                  isBordered
                />
              </div>

              <p class="form-create__label">Information must have</p>
              <div
                v-if="keyword && keyword.attributesFromParent.includes('information_must_have')"
                class="form-create__info"
              >
                Parent keyword requirements are output
              </div>
              <div class="input-container">
                <InputTextarea
                  :set-value="formDataToSend"
                  :id="'information_must_have'"
                  :label="'information_must_have'"
                  :placeholder="''"
                  :error-messages="validationErrors['informationMustHave']"
                  isBordered
                />
              </div>

              <p class="form-create__label">Information can be added</p>
              <div
                v-if="keyword && keyword.attributesFromParent.includes('information_can_be_added')"
                class="form-create__info"
              >
                Parent keyword requirements are output
              </div>
              <div class="input-container">
                <InputTextarea
                  :set-value="formDataToSend"
                  :id="'information_can_be_added'"
                  :label="'information_can_be_added'"
                  :placeholder="''"
                  :error-messages="validationErrors['informationCanBeAdded']"
                  isBordered
                />
              </div>

              <p class="form-create__label">Planned labor intensity in hours</p>
              <div
                v-if="
                  keyword &&
                  keyword.attributesFromParent.includes('planned_labor_intensity_in_hours')
                "
                class="form-create__info"
              >
                Parent keyword requirements are output
              </div>
              <div class="input-container">
                <InputText
                  :set-value="formDataToSend"
                  :id="'planned_labor_intensity_in_hours'"
                  :label="'planned_labor_intensity_in_hours'"
                  :type="'text'"
                  :placeholder="'Planned labor intensity in hours'"
                  :error-messages="validationErrors['planned_labor_intensity_in_hours']"
                  isBordered
                />
              </div>

              <p class="form-create__label">AI Requirements</p>
              <div
                v-if="keyword && keyword.attributesFromParent.includes('ai_requirements')"
                class="form-create__info"
              >
                Parent keyword requirements are output
              </div>
              <div class="input-container">
                <TextEditor
                  :set-value="formDataToSend"
                  :id="'ai_requirements'"
                  :placeholder="''"
                  :error-messages="validationErrors['aiRequirements']"
                />
              </div>
              <div class="form-create__btn">
                <MainButton @click.native="handleAiRequirements">Get AI Requirements</MainButton>
              </div>
            </div>
          </transition>

          <div v-if="keyword && keyword.category[0]" class="form-create__tree">
            <div class="form-create__tree-title">Keyword Hierarchy</div>
            <div class="form-create__tree-item first">
              <router-link :to="handleToCategory(keyword.category[0].id)">
                {{ keyword.category[0].fullName }}
              </router-link>
            </div>

            <div v-if="keyword.parentId" class="form-create__tree-item second">
              <router-link :to="handleToKeyword(keyword.parent.id)">
                {{ keyword.parent.keyword }}
              </router-link>
            </div>

            <div v-else class="form-create__tree-item second active">
              {{ keyword.keyword }}
            </div>
            <div v-if="keyword.children[0] && !keyword.parentId" class="checkbox-container">
              <div
                v-for="item in keyword.children[0]"
                :key="item.id"
                :class="['form-create__tree-item third', keyword.id == item.id && active]"
              >
                <router-link :to="handleToKeyword(item.id)">
                  {{ item.keyword }}
                </router-link>
              </div>
            </div>
            <div v-if="keyword.parentId" class="checkbox-container">
              <div :class="['form-create__tree-item third active']">
                {{ keyword.keyword }}
              </div>
            </div>
          </div>

          <div v-if="keyword && keyword.templates[0]" class="form-create__templates">
            <div class="form-create__templates-title">Linked Templates</div>
            <div class="checkbox-container">
              <div
                v-for="item in keyword.templates[0]"
                :key="item.id"
                class="form-create__templates-item"
              >
                <router-link :to="handleToTemplate(item.id)">
                  {{ item.name }}
                </router-link>
                <div class="">Status: {{ item.status.name }}</div>
                <div class="">Published date: {{ item.publishedAt }}</div>
                <a :href="`${publicUrl}${item.url}`">Public template link</a>
              </div>
            </div>
          </div>

          <div v-if="error.message != undefined" class="form-create__error">
            {{ error.message }}
          </div>
          <div class="form-create__btn">
            <MainButton :type="'submit'">Submit</MainButton>
          </div>
        </form>

        <MainModal v-if="copyModal" :on-close="handleCloseCopyModal" :modal-center="true">
          <div class="list__confirm">
            <p class="form-create__label">Keyword</p>
            <div class="input-container">
              <div class="form-create__keyword">
                <input
                  type="text"
                  @focus="handleFocus"
                  @blur="handleBlur"
                  @input="debouncedHandleInput"
                  v-model="copy_name"
                  :class="['form-create__keyword-input']"
                />

                <div v-if="attributes_filled && copy_id" class="form-create__keyword-text green">
                  Attributes filled
                </div>
                <div v-if="!attributes_filled && copy_id" class="form-create__keyword-text red">
                  Attributes no filled
                </div>

                <div v-if="keywordList" class="form-create__keyword-list">
                  <template v-if="keywordArray.length > 0">
                    <div
                      @click="handleChooseItem(item)"
                      v-for="item in keywordArray"
                      :key="item.id"
                      class="form-create__keyword-item"
                    >
                      {{ item.keyword.toLowerCase() }}
                    </div>
                  </template>
                  <div v-else class="form-create__keyword-item">NO keywords</div>
                </div>
                <Notification :error-messages="validationErrors['keywordId']" />
              </div>
            </div>

            <div class="list__confirm-buttons">
              <div class="list__confirm-button">
                <MainButton buttonWhite @click.native="handleCloseCreateModal" :button-cancel="true"
                  >CANCEL</MainButton
                >
              </div>
              <div class="list__confirm-button">
                <MainButton @click.native="copyKey">COPY</MainButton>
              </div>
            </div>
          </div>
        </MainModal>

        <ModalError
          :error-modal="false"
          :handle-close-error-modal="
            () => {
              this.error = {};
            }
          "
        />
      </div>
    </ContentContainer>
    <FetchSpinnerModal v-if="loadingProcess" />
  </Main>
</template>

<script>
import { mapState } from "vuex";
import ROUTE from "~/constants/routes";
import templatesApi from "~/api/templates";
import categoriesApi from "~/api/categories";
import referencesApi from "~/api/references";
import { debounce } from "lodash";
import { getSite } from "~/helpers/userAuth";

import IconSelect from "~/assets/images/icons/select-icon.svg";

import Main from "~/templates/Main.vue";
import FetchSpinnerModal from "~/components/molecules/FetchSpinnerModal.vue";
import ToolsPanel from "~/components/molecules/ToolsPanel.vue";
import ContentContainer from "~/components/molecules/ContentContainer.vue";
import MainButton from "~/components/atoms/buttons/MainButton.vue";
import InputText from "~/components/atoms/inputs/InputText.vue";
import ModalError from "~/components/molecules/ModalError.vue";
import InputRadio from "~/components/atoms/inputs/InputRadio.vue";
import Notification from "~/components/molecules/Notification.vue";
import InputTextarea from "~/components/atoms/inputs/InputTextarea.vue";
import MainCheckbox from "~/components/atoms/inputs/MainCheckbox.vue";
import TextEditor from "~/components/atoms/inputs/TextEditor.vue";
import MainModal from "~/components/molecules/MainModal.vue";

export default {
  name: "KeywordEdit",
  metaInfo: {
    title: "Keyword Edit",
  },
  data() {
    return {
      publicUrl: "",
      siteId: null,
      keywordList: false,
      keywordArray: [],
      copyModal: false,
      attributes_filled: false,
      parentList: false,
      isAllFields: false,
      attributeSearch: "",
      attributesTree: [],
      attributesAll: [],
      formDataToSend: {
        keyword: "",
        category: null,
        size: {},
        type: {},
        attributes: [],
        months: {
          1: false,
          2: false,
          3: false,
          4: false,
          5: false,
          6: false,
          7: false,
          8: false,
          9: false,
          10: false,
          11: false,
          12: false,
          13: false,
        },
        information_must_have: "",
        information_can_be_added: "",
        examples: "",
        pages: "",
        target_audience: "",
        preview_requirements: "",
        planned_labor_intensity_in_hours: "",
        parent_name: "",
        parent_id: null,
        ai_requirements: "",
        key_points: "",
        template_requirements: "",
        template_diagram: "",
        template_overview: "",
        indents_and_margins: "",
        typography: "",
        images: "",
      },
      months: [
        { id: 1, name: "January" },
        { id: 2, name: "February" },
        { id: 3, name: "March" },
        { id: 4, name: "April" },
        { id: 5, name: "May" },
        { id: 6, name: "June" },
        { id: 7, name: "July" },
        { id: 8, name: "August" },
        { id: 9, name: "September" },
        { id: 10, name: "October" },
        { id: 11, name: "November" },
        { id: 12, name: "December" },
        { id: 13, name: "NO Data" },
      ],
      parentArray: [],
      keyword: null,
      categoriesList: [],
      categorySearch: "",
      attributeArray: [],
      isLoadedKeyword: false,
      copy_name: "",
      copy_id: null,
    };
  },

  components: {
    Main,
    FetchSpinnerModal,
    ToolsPanel,
    ContentContainer,
    MainButton,
    InputText,
    ModalError,
    InputRadio,
    Notification,
    MainCheckbox,
    InputTextarea,
    TextEditor,
    IconSelect,
    MainModal,
  },
  mounted() {
    this.getAttributesAll();
    this.getCategories();
    this.getAttributes();
    this.getAttributesAll();

    this.siteId = getSite().id;

    if (this.siteId == 1) {
      this.publicUrl = "https://thegoodocs.com";
    } else if (this.siteId == 2) {
      this.publicUrl = "https://www.elegantflyer.com";
    }
  },
  computed: {
    ...mapState("templates", ["error", "loadingProcess", "validationErrors"]),
    ...mapState("references", ["templatesSizesList", "templatesTypesList"]),
    getFirstLink() {
      return ROUTE.KEYWORDS_LIST.replace(":page", 1);
    },
    getId() {
      return this.$route.params.id;
    },
  },
  methods: {
    getAttributesAll() {
      try {
        referencesApi.getTemplatesAttributesAll().then((res) => {
          this.attributesAll = res.data.items;
          this.getItem();
        });
      } catch (error) {
        console.log(error);
      }
    },
    async getData() {
      await referencesApi
        .getTemplatesSizesAll()
        .then((res) => {
          this.$store.commit("references/setTemplatesSizesList", res.data.items);
        })
        .catch((error) => {})
        .finally(() => {});

      await referencesApi
        .getTemplatesTypes()
        .then((res) => {
          this.$store.commit("references/setTemplatesTypesList", res.data[0].data);
        })
        .catch((error) => {})
        .finally(() => {});

      await this.setSize();
      await this.setType();
    },
    setSize() {
      const size = this.templatesSizesList;
      for (let i = 0; size.length > i; i++) {
        if (this.formDataToSend.size[size[i].id] == undefined) {
          this.$set(this.formDataToSend.size, size[i].id, false);
        }
      }
    },
    setType() {
      const type = this.templatesTypesList;
      console.log(this.templatesTypesList);
      for (let i = 0; type.length > i; i++) {
        if (this.formDataToSend.type[type[i].id] == undefined) {
          this.$set(this.formDataToSend.type, type[i].id, false);
        }
      }
    },
    setAttributes() {
      const attributes = this.attributeArray;
      for (let i = 0; attributes.length > i; i++) {
        if (this.formDataToSend.attributes[attributes[i].id] == undefined) {
          this.$set(this.formDataToSend.attributes, attributes[i].id, false);
        }
      }
    },
    handleSubmit() {
      const data = {
        keyword: this.formDataToSend.keyword.trim(),
        category_id: this.formDataToSend.category,
        // requirements_link: this.formDataToSend.requirements_link.trim(),
        key_points: this.formDataToSend.key_points.trim(),
        information_must_have: this.formDataToSend.information_must_have.trim(),
        information_can_be_added: this.formDataToSend.information_can_be_added.trim(),
        pages: this.formDataToSend.pages.trim(),
        target_audience: this.formDataToSend.target_audience.trim(),
        preview_requirements: this.formDataToSend.preview_requirements.trim(),
        planned_labor_intensity_in_hours:
          this.formDataToSend.planned_labor_intensity_in_hours.trim(),
        parent_id: this.formDataToSend.parent_id,
        ai_requirements: this.formDataToSend.ai_requirements,

        template_requirements: this.formDataToSend.template_requirements,
        template_diagram: this.formDataToSend.template_diagram,
        template_overview: this.formDataToSend.template_overview,
        images: this.formDataToSend.images,
        typography: this.formDataToSend.typography,
        indents_and_margins: this.formDataToSend.indents_and_margins,
      };

      const examples = this.formDataToSend.examples.toLowerCase().split(/\r\n|\r|\n/g);
      const examplesSend = examples.filter((str) => str.trim() !== "");

      data.examples = examplesSend;

      const size = Object.keys(this.formDataToSend.size);
      if (size.length) {
        let sizeSend = [];
        for (let i = 0; size.length > i; i++) {
          if (this.formDataToSend.size[size[i]] == true) {
            sizeSend.push(size[i]);
          }
        }
        if (sizeSend.length > 0) {
          data.sizes = sizeSend;
        }
      }

      data.attributes = [];
      if (this.formDataToSend.attributes.length) {
        this.formDataToSend.attributes.forEach((item, index) => {
          data.attributes.push(item.id);
        });
      }

      const type = Object.keys(this.formDataToSend.type);
      if (type.length) {
        let typeSend = [];
        for (let i = 0; type.length > i; i++) {
          if (this.formDataToSend.type[type[i]] == true) {
            typeSend.push(type[i]);
          }
        }
        if (typeSend.length > 0) {
          data.types = typeSend;
        }
      }

      const months = Object.keys(this.formDataToSend.months);
      if (months.length) {
        let monthsSend = [];
        for (let i = 0; months.length > i; i++) {
          if (this.formDataToSend.months[months[i]] == true) {
            monthsSend.push(months[i]);
          }
        }
        if (monthsSend.length > 0) {
          data.seasonality = monthsSend;
        }
      }

      this.$store.commit("templates/setLoading", true);
      const url = `/${this.getId}`;
      try {
        templatesApi
          .editKeywords(url, data)
          .then((res) => {
            if (res.success) {
              this.$store.commit("templates/setError", {});
              this.$store.commit("templates/setValidationErrors", {});
              this.handleBackToList();
            }
          })
          .catch((error) => {
            const { data } = error.response;
            this.$store.commit("templates/setError", data);
            if (data.errors) {
              this.$store.commit("templates/setValidationErrors", data.errors);
            }
          })
          .finally(() => {
            this.$store.commit("templates/setLoading", false);
          });
      } catch (error) {
        console.log(error);
      }
    },
    handleBackToList() {
      this.$router.push({
        path: ROUTE.KEYWORDS_LIST.replace(":page", 1),
      });
    },
    handleToCategory(id) {
      return ROUTE.CATEGORY_EDIT.replace(":id", id);
    },
    handleToKeyword(id) {
      return ROUTE.KEYWORDS_EDIT.replace(":id", id);
    },
    handleToTemplate(id) {
      return ROUTE.TEMPLATE_EDIT.replace(":id", id);
    },
    handleCloseCopyModal() {
      this.copy_id = null;
      this.copy_name = "";

      this.copyModal = false;
    },
    handleOpenCopyModal() {
      this.copyModal = true;
    },
    handleFocus() {
      this.keywordList = true;
      this.debouncedHandleInput();
    },
    handleBlur() {
      setTimeout(() => {
        this.keywordList = false;
        this.keywordArray = [];
      }, 500);
    },
    debouncedHandleInput: debounce(function () {
      this.handleInput();
    }, 300),
    handleInput() {
      const data = {};
      data.search = this.copy_name.trim();
      data.perPage = 20;
      templatesApi
        .getKeywordsListFilter(``, data)
        .then((res) => {
          this.keywordArray = res.data.data.items.data;
          this.keywordArray.sort((a, b) => {
            if (a.keyword < b.keyword) {
              return -1;
            }
            if (a.keyword > b.keyword) {
              return 1;
            }
            return 0;
          });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {});
    },
    handleChooseItem(item) {
      this.copy_id = item.id;
      this.copy_name = item.keyword;
      this.attributes_filled = item.attributesFilled;

      console.log(item);
    },
    copyKey() {
      if (this.copy_name) {
        this.getItem(this.copy_id);
      }
    },
    getItem(id = false) {
      let url;
      if (id) {
        url = `/${id}`;
      } else {
        url = `/${this.getId}?appendKeywordAhrefsLinks=1`;
      }

      this.$store.commit("templates/setLoading", true);
      try {
        templatesApi.getKeywordsItem(url).then((res) => {
          this.$store.commit("templates/setError", {});
          this.$store.commit("templates/setValidationErrors", {});
          const data = res.data;
          this.keyword = res.data;
          if (!id) {
            this.formDataToSend.keyword = data.keyword;
          }
          if (data.category[0]) {
            this.formDataToSend.category = data.categoryId == 0 ? null : data.categoryId;
          }
          this.formDataToSend.information_must_have = data.informationMustHave || "";
          this.formDataToSend.key_points = data.keyPoints || "";
          this.formDataToSend.information_can_be_added = data.informationCanBeAdded || "";
          this.formDataToSend.pages = data.pages || "";
          this.formDataToSend.target_audience = data.targetAudience || "";
          this.formDataToSend.preview_requirements = data.previewRequirements || "";
          this.formDataToSend.planned_labor_intensity_in_hours =
            data.plannedLaborIntensityInHours || "";
          this.formDataToSend.ai_requirements = data.aiRequirements || "";

          this.formDataToSend.template_requirements = data.templateRequirements || "";
          this.formDataToSend.template_diagram = data.templateDiagram || "";
          this.formDataToSend.template_overview = data.templateOverview || "";

          this.formDataToSend.indents_and_margins = data.indentsAndMargins || "";
          this.formDataToSend.typography = data.typography || "";
          this.formDataToSend.images = data.images || "";

          if (data.parent && !id) {
            this.formDataToSend.parent_name = data.parent.keyword;
            this.formDataToSend.parent_id = data.parent.id;
          }

          if (data.examples) {
            this.formDataToSend.examples = data.examples.join("\n");
          }

          const type = data.allowedTypes?.[0];
          if (type != undefined && type.length > 0) {
            for (let i = 0; type.length > i; i++) {
              this.$set(this.formDataToSend.type, type[i].id, true);
            }
          }

          const size = data.allowedSizes?.[0];
          if (size != undefined && size.length > 0) {
            for (let i = 0; size.length > i; i++) {
              this.$set(this.formDataToSend.size, size[i].id, true);
            }
          }

          const attributes = data.allowedAttributes?.[0];

          if (attributes && attributes.length) {
            const attributesPush = [];

            attributes.forEach((el) => {
              const item = this.attributesAll.find((u) => u.id == el.id);
              if (item) {
                attributesPush.push(item);
              }
            });

            this.formDataToSend.attributes = attributesPush;
          }

          const months = data.seasonality;
          if (months != undefined && months.length > 0) {
            for (let i = 0; months.length > i; i++) {
              this.formDataToSend.months[months[i]] = true;
            }
          }

          this.getData();

          this.copyModal = false;
          this.$store.commit("templates/setLoading", false);

          setTimeout(() => {
            this.isLoadedKeyword = true;
          }, 0);
        });
      } catch (error) {
        this.$store.commit("templates/setLoading", false);
        console.log(error);
      }
    },
    getCategories() {
      if (this.categorySearch.trim().length > 2) {
        const data = {
          search: this.categorySearch.trim(),
        };
        try {
          categoriesApi.getCategoriesListFilter("?status=1", data).then((res) => {
            const data = res.data.items.data;
            this.categoriesList = data;
          });
        } catch (error) {
          console.log(error);
        }
      } else {
        try {
          categoriesApi.getCategoriesActive().then((res) => {
            const data = res.data.items;
            this.categoriesList = data;
          });
        } catch (error) {
          console.log(error);
        }
      }
    },
    debouncedGetCategories: debounce(function () {
      this.getCategories();
    }, 300),

    getParent() {
      const data = {};
      data.search = this.formDataToSend.parent_name.trim();
      data.perPage = 20;
      templatesApi
        .getKeywordsListFilter(``, data)
        .then((res) => {
          const array = res.data.data.items.data;
          const setArray = [];
          for (let i = 0; array.length > i; i++) {
            if (!array[i].parentId) {
              setArray.push(array[i]);
            }
          }
          this.parentArray = setArray;

          this.parentArray.sort((a, b) => {
            if (a.keyword < b.keyword) {
              return -1;
            }
            if (a.keyword > b.keyword) {
              return 1;
            }
            return 0;
          });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {});
    },
    debouncedGetParent: debounce(function () {
      this.getParent();
    }, 300),

    handleFocusParent() {
      this.parentList = true;
      this.debouncedGetParent();
    },

    handleBlurParent() {
      setTimeout(() => {
        this.parentList = false;
        this.parentArray = [];
      }, 500);
    },

    handleChooseItemParent(item) {
      this.formDataToSend.parent_id = item.id;
      this.formDataToSend.parent_name = item.keyword;
    },

    handleAiRequirements() {
      if (this.formDataToSend.keyword.length > 2) {
        this.$store.commit("templates/setLoading", true);
        const url = `?keyword=${this.formDataToSend.keyword}`;
        try {
          templatesApi.getAiRequirements(url).then((res) => {
            this.$store.commit("templates/setLoading", false);
            console.log(res);
            if (res.data.success) {
              this.formDataToSend.ai_requirements = res.data.data.response;
            }
          });
        } catch (error) {
          console.log(error);
          this.$store.commit("templates/setLoading", false);
        }
      }
    },

    beforeEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
    },
    enter(el, done) {
      const height = el.scrollHeight + "px";
      el.style.transition = "height 0.3s ease, opacity 0.3s ease";
      el.style.height = height;
      el.style.opacity = "1";
      el.addEventListener("transitionend", done);
    },
    leave(el, done) {
      el.style.transition = "height 0.3s ease, opacity 0.3s ease";
      el.style.height = "0";
      el.style.opacity = "0";
      el.addEventListener("transitionend", done);
    },
    toggleAllFields() {
      this.isAllFields = !this.isAllFields;
    },
    deleteParent() {
      this.formDataToSend.parent_name = "";
      this.formDataToSend.parent_id = null;
    },
    getAttributes() {
      let url = "";
      const search = this.attributeSearch.trim();
      if (search.length) {
        url = `?name=${search}`;
      }
      try {
        referencesApi.getTemplatesAttributesTree(url).then((res) => {
          this.attributesTree = res.data.items;
        });
      } catch (error) {
        console.log(error);
      }
    },
    debouncedGetAttributes: debounce(function () {
      this.getAttributes();
    }, 300),
    addAttribute(item) {
      const exists = this.formDataToSend.attributes.some((a) => a.id === item.id);
      if (!exists) {
        this.formDataToSend.attributes.push(item);
      }
    },
    deleteAttribute(index) {
      this.formDataToSend.attributes.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.input-select {
  max-width: 300px;
}
.checkbox-container {
  @include scrollBar;

  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 20px;

  &_margin {
    margin-top: 20px;
  }
}
.checkbox-container__item {
  padding: 5px 0;
}
.relative {
  position: relative;
}
.ahrefs-link {
  position: absolute;
  top: 10px;
  right: 5px;
  background: #3a80f9;
  padding: 5px 10px;
  border-radius: 10px;
  color: #fff;
}
.form-create {
  &__check-item {
    padding: 10px;
    display: flex;
    align-items: center;

    &.child {
      padding-left: 30px;
    }
  }

  &__check-item-add {
    margin-left: 20px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 5px;
    background: #3a80f9;
    border-radius: 50px;
    color: #fff;
    font-size: 18px;
    font-weight: 700;
  }

  &__chosen-items {
    display: flex;
    flex-wrap: wrap;
  }

  &__search {
    font-size: 18px;
    margin-top: 20px;
    width: 200px;
    padding: 5px 10px;
  }

  &__chosen-item {
    margin: 0 10px 10px 0;
    padding: 5px;
    background: gainsboro;
    border-radius: 10px;
    display: flex;
    align-items: center;
  }

  &__delete-parent {
    background: #3a80f9;
    cursor: pointer;
    padding: 5px 10px;
    color: #fff;
    border-radius: 5px;
    width: fit-content;
  }

  &__all-button {
    margin: 20px 0;
    padding: 10px 20px;
    background: #e5e5e7;
    font-size: 20px;
    border-radius: 5px;
    width: fit-content;
    cursor: pointer;

    svg {
      margin-left: 10px;
    }

    &.active {
      svg {
        transform: rotate(180deg);
      }
    }
  }

  &__all-container {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 5px;
    margin-bottom: 30px;
  }
  &__search {
    font-size: 18px;
    margin-top: 20px;
    width: 400px;
    padding: 5px 10px;
  }

  &__info {
    padding: 10px 0;
    font-size: 20px;
    font-weight: 700;
    color: rgb(202, 22, 22);
  }

  &__keyword-input {
    width: 100%;
    font-size: 18px;
    padding: 5px 10px;
    margin-bottom: 10px;
  }

  &__keyword {
    position: relative;
    margin-bottom: 10px;
  }

  &__keyword-list {
    position: absolute;
    left: 0;
    top: calc(100% + 5px);
    border: 1px solid gray;
    background: #fff;
    padding: 10px 0 10px;
    z-index: 10;
  }

  &__keyword-item {
    font-size: 16px;
    padding: 5px 20px;
    cursor: pointer;

    &:hover {
      background: rgb(216, 213, 213);
    }
  }

  &__tree {
    margin-bottom: 30px;
    background: rgb(243, 243, 243);
    padding: 15px;
    border-radius: 10px;
  }

  &__tree-title {
    font-size: 25px;
    font-weight: 700;
    padding-bottom: 20px;
  }

  &__tree-item {
    padding-bottom: 10px;

    &.first {
      font-size: 22px;
    }

    &.second {
      font-size: 20px;
      padding-left: 30px;

      &.active {
        font-weight: 700;
      }
    }

    &.third {
      font-size: 18px;
      padding-left: 60px;

      &.active {
        font-weight: 700;
      }
    }
  }

  &__templates {
    margin-bottom: 30px;
    background: rgb(243, 243, 243);
    padding: 15px;
    border-radius: 10px;
  }

  &__templates-title {
    font-size: 25px;
    font-weight: 700;
    padding-bottom: 20px;
  }

  &__templates-item {
    font-size: 20px;
    padding: 10px;
    background: rgb(219, 219, 219);
    border-radius: 10px;
    margin-bottom: 10px;
    margin-right: 10px;
  }

  &__keyword-text {
    padding: 10px 15px;
    border-radius: 5px;

    &.green {
      color: #155724;
      background-color: #d4edda;
      border-color: #c3e6cb;
    }

    &.red {
      color: #721c24;
      background-color: #f8d7da;
      border-color: #f5c6cb;
    }
  }
}
</style>
